import clsx from 'clsx';
import React, { useId } from 'react';

import CaretIcon from '../../../assets/Svg/CaretIcon';
import { COLORS } from '../../../utils/constants/colors';
import { FIELD_CARET_SIZE } from './constants';
import { FieldTextProps, FieldTriggerProps } from './types';

import styles from './FieldTrigger.module.scss';

// aria-disabled + tabIndex -1 (not `disabled`) keeps hover tooltips on truncated text working in the read-only state.
// labelledBy names the button "<label> <own content>": a lone aria-labelledby would replace the value in the accessible name.
const FieldTrigger: React.FC<FieldTriggerProps> = ({
    disabled = false,
    icon,
    labelledBy,
    className,
    onClick,
    children,
    ...buttonProps
}) => {
    const ownId = useId();
    return (
        <button
            {...buttonProps}
            id={ownId}
            aria-labelledby={labelledBy ? `${labelledBy} ${ownId}` : undefined}
            type="button"
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : buttonProps.tabIndex}
            className={clsx(styles.FieldBox, { [styles.FieldBoxReadOnly]: disabled }, className)}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onClick?.(e);
            }}
        >
            {children}
            {!disabled && icon && <span className={styles.FieldIcon}>{icon}</span>}
        </button>
    );
};

export const FieldText: React.FC<FieldTextProps> = ({ isPlaceholder = false, chip = false, className, ...spanProps }) => (
    <span
        {...spanProps}
        className={clsx(
            styles.FieldText,
            { [styles.FieldPlaceholder]: isPlaceholder, [styles.FieldChip]: chip && !isPlaceholder },
            className,
        )}
    />
);

export const FieldCaret: React.FC<{ open?: boolean }> = ({ open = false }) => (
    <span className={clsx(styles.FieldCaret, { [styles.FieldCaretOpen]: open })}>
        <CaretIcon colour={COLORS.LIGHT_GREY} height={FIELD_CARET_SIZE.HEIGHT} width={FIELD_CARET_SIZE.WIDTH} />
    </span>
);

export default FieldTrigger;
